import { Logo } from '@/components/logo.component';
import { LogoLight } from '@/components/logo-light.component';
import { PanelToggleButton } from '@/components/ui/panel-toggle';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useSidebar } from '../sidebar-provider.component';
import { RAIL_CELL } from './tabs-sidebar.styles';
import { BRAND } from '@/lib/brand';

/**
 * The tabbed sidebar's top row, 28px high open or folded: open, the Cleanlist
 * logo (home) on the left and Hide sidebar on the right; folded, Show sidebar
 * alone, the Cleanlist leaf at rest that turns into the drawer icon on hover
 * or keyboard focus. Show opens the sidebar wherever the user is, beside the
 * Clu dock too, which moves right to make room. The
 * theme's `dark` class on <html> picks the logo and the leaf, so a theme
 * change re-renders nothing. On the page rail, Show sidebar is one of its
 * cells.
 */
export function TabsSidebarHeader() {
  const { isCollapsed, expand, collapse } = useSidebar();

  if (isCollapsed) {
    return (
      <SidebarToggle
        label='Show sidebar'
        onClick={expand}
        tooltipSide='right'
        className={RAIL_CELL}
        resting={
          <>
            <img
              src='/cleanlist-icon-black.svg'
              alt=''
              className='h-5 w-5 object-contain dark:hidden'
            />
            <img
              src='/cleanlist-icon-white.svg'
              alt=''
              className='hidden h-5 w-5 object-contain dark:block'
            />
          </>
        }
      />
    );
  }
  return (
    <div className='flex h-7 shrink-0 items-center justify-between'>
      <Link
        to='/'
        aria-label={BRAND.name}
        data-sidebar-logo
        className='flex h-7 items-center rounded-md pr-1 pl-2.5 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50'
      >
        <Logo className='h-6 w-auto dark:hidden' />
        <LogoLight className='hidden h-6 w-auto dark:block' />
      </Link>
      <SidebarToggle
        label='Hide sidebar'
        onClick={collapse}
        tooltipSide='bottom'
      />
    </div>
  );
}

/** The sidebar's drawer toggle, with its label as a tooltip. */
function SidebarToggle({
  label,
  onClick,
  tooltipSide,
  resting,
  className,
}: {
  label: string;
  onClick: () => void;
  tooltipSide: 'right' | 'bottom';
  resting?: ReactNode;
  className?: string;
}) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <PanelToggleButton
          label={label}
          side='left'
          resting={resting}
          onClick={onClick}
          className={className}
        />
      </TooltipTrigger>
      <TooltipContent side={tooltipSide} sideOffset={6}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
